import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  Snackbar,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from '../../app/LanguageContext';
import { getPolaroidSizesSettings, updatePolaroidSizesSettings } from '../../app/api';

const PREVIEW_MAX_WIDTH = 88;

const emptyRow = () => ({
  label: '',
  width_mm: '',
  height_mm: '',
  image_width_mm: '',
  image_height_mm: '',
  image_top_mm: '',
  image_left_mm: '',
});

function deriveCodeFromLabel(label, index, usedCodes) {
  const slug = String(label || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .toUpperCase()
    .slice(0, 28);
  let base = slug.length >= 2 ? slug : `SIZE_${index + 1}`;
  let code = base;
  let suffix = 2;
  while (usedCodes.has(code)) {
    code = `${base}_${suffix}`;
    suffix += 1;
  }
  usedCodes.add(code);
  return code;
}

function SizePreview({ row }) {
  const w = Number(row.width_mm) || 1;
  const h = Number(row.height_mm) || 1;
  const iw = Number(row.image_width_mm) || 0;
  const ih = Number(row.image_height_mm) || 0;
  const it = Number(row.image_top_mm) || 0;
  const il = Number(row.image_left_mm) || 0;
  const scale = PREVIEW_MAX_WIDTH / w;
  const previewH = h * scale;

  return (
    <Box
      sx={{
        width: PREVIEW_MAX_WIDTH,
        height: previewH,
        maxHeight: '100%',
        bgcolor: '#fff',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 1,
        position: 'relative',
        flexShrink: 0,
      }}
    >
      {iw > 0 && ih > 0 && (
        <Box
          sx={{
            position: 'absolute',
            left: il * scale,
            top: it * scale,
            width: iw * scale,
            height: ih * scale,
            bgcolor: 'grey.300',
          }}
        />
      )}
    </Box>
  );
}

function DimensionField({ label, value, onChange }) {
  return (
    <TextField
      size="small"
      type="number"
      label={label}
      value={value}
      onChange={onChange}
      fullWidth
      inputProps={{ min: 0, step: 0.1 }}
    />
  );
}

function SizeSettingsRow({ row, index, onUpdate, onRemove, canRemove, translate }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 1.5, sm: 2 },
        mb: 1.5,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            alignSelf: 'stretch',
            py: 0.5,
          }}
        >
          <SizePreview row={row} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              size="small"
              label={translate('polaroid_creator.col_label')}
              value={row.label}
              onChange={(e) => onUpdate(index, 'label', e.target.value)}
              sx={{ flex: 1, minWidth: 0 }}
            />
            {canRemove && (
              <IconButton
                size="small"
                onClick={() => onRemove(index)}
                aria-label={translate('common.delete')}
                sx={{ flexShrink: 0 }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
              gap: 1,
            }}
          >
            <DimensionField
              label={translate('polaroid_creator.col_width')}
              value={row.width_mm}
              onChange={(e) => onUpdate(index, 'width_mm', e.target.value)}
            />
            <DimensionField
              label={translate('polaroid_creator.col_height')}
              value={row.height_mm}
              onChange={(e) => onUpdate(index, 'height_mm', e.target.value)}
            />
            <DimensionField
              label={translate('polaroid_creator.col_img_width')}
              value={row.image_width_mm}
              onChange={(e) => onUpdate(index, 'image_width_mm', e.target.value)}
            />
            <DimensionField
              label={translate('polaroid_creator.col_img_height')}
              value={row.image_height_mm}
              onChange={(e) => onUpdate(index, 'image_height_mm', e.target.value)}
            />
            <DimensionField
              label={translate('polaroid_creator.col_img_top')}
              value={row.image_top_mm}
              onChange={(e) => onUpdate(index, 'image_top_mm', e.target.value)}
            />
            <DimensionField
              label={translate('polaroid_creator.col_img_left')}
              value={row.image_left_mm}
              onChange={(e) => onUpdate(index, 'image_left_mm', e.target.value)}
            />
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}

export default function PolaroidCreatorSettingsPage() {
  const { translate } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([emptyRow()]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPolaroidSizesSettings();
      const list = res?.data?.sizes ?? res?.sizes ?? [];
      if (Array.isArray(list) && list.length > 0) {
        setRows(list.map((s) => ({
          _code: s.code ?? '',
          label: s.label ?? '',
          width_mm: s.width_mm ?? '',
          height_mm: s.height_mm ?? '',
          image_width_mm: s.image_width_mm ?? '',
          image_height_mm: s.image_height_mm ?? '',
          image_top_mm: s.image_top_mm ?? '',
          image_left_mm: s.image_left_mm ?? '',
        })));
      } else {
        setRows([emptyRow()]);
      }
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
      setRows([emptyRow()]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateRow = (index, field, value) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  };

  const addRow = () => setRows((prev) => [...prev, emptyRow()]);

  const removeRow = (index) => {
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const buildPayload = () => {
    const usedCodes = new Set();
    return rows.map((r, index) => {
      const label = String(r.label || '').trim();
      let code = String(r._code || '').trim().toUpperCase();
      if (!code) {
        code = deriveCodeFromLabel(label, index, usedCodes);
      } else {
        usedCodes.add(code);
      }
      return {
        code,
        label,
        width_mm: Number(r.width_mm),
        height_mm: Number(r.height_mm),
        image_width_mm: Number(r.image_width_mm),
        image_height_mm: Number(r.image_height_mm),
        image_top_mm: Number(r.image_top_mm),
        image_left_mm: Number(r.image_left_mm),
      };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await updatePolaroidSizesSettings({ sizes: buildPayload() });
      setSnackbar({ open: true, message: translate('common.saved'), severity: 'success' });
      await load();
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {translate('polaroid_creator.settings_hint')}
      </Typography>
      {rows.map((row, index) => (
        <SizeSettingsRow
          key={index}
          row={row}
          index={index}
          onUpdate={updateRow}
          onRemove={removeRow}
          canRemove={rows.length > 1}
          translate={translate}
        />
      ))}
      <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button startIcon={<AddIcon />} onClick={addRow}>
          {translate('polaroid_creator.add_size')}
        </Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {translate('common.save')}
        </Button>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
